import { api } from './request'

export interface AdminUser {
  id: number
  email: string
  username: string
  display_name: string
  avatar: string
  bio: string
  website: string
  company: string
  location: string
  email_verified: boolean
  status: number
  is_admin?: boolean
  is_super_admin?: boolean
  created_at: string
  updated_at: string
}

export interface AdminApiKey {
  id: number
  name: string
  key: string
  scopes: string[]
  status: number
  last_used_at: string | null
  created_at: string
  updated_at: string
}

export const adminApi = {
  // Admin login (dedicated admin login API)
  login(email: string, password: string) {
    return api.post('/admin/login', { email, password })
  },

  listUsers(page = 1, pageSize = 20, search = '') {
    return api.get<{ list: AdminUser[]; total: number; page: number; page_size: number }>(
      '/admin/users',
      { params: { page, page_size: pageSize, search } }
    )
  },

  updateUser(id: number, data: Partial<AdminUser>) {
    return api.put<AdminUser>(`/admin/users/${id}`, data)
  },

  setAdmin(id: number, isAdmin: boolean) {
    return api.put<AdminUser>(`/admin/users/${id}/admin`, { is_admin: isAdmin })
  },

  check() {
    return api.get<{ is_admin: boolean; is_super_admin: boolean }>('/admin/check')
  },

  getDownloadAnalytics(days = 30) {
    return api.get<{
      days: number
      summary: { date: string; count: number }[]
      by_repo: { date: string; repository_id: number; repo_name: string; count: number }[]
    }>('/admin/analytics/downloads', { params: { days } })
  },

  getPageViewAnalytics(days = 30) {
    return api.get<{
      days: number
      pv_summary: { date: string; count: number }[]
      uv_summary: { date: string; count: number }[]
      total_uv: number
      prev_pv: number
      prev_uv: number
      new_visitors: { date: string; count: number }[]
      bounce_rate: { date: string; rate: number }[]
      total_bounce_rate: number
      sources: { name: string; count: number }[]
      devices: { name: string; count: number }[]
      countries: { name: string; count: number }[]
      hourly: { hour: number; count: number }[]
      landing_pages: { path: string; count: number }[]
      referers: { domain: string; count: number }[]
      retention: { date: string; rate: number }[]
    }>('/admin/analytics/pageviews', { params: { days } })
  },
}

// Admin content & catalog management
export const adminContentApi = {
  // repositories
  listRepositories(params: { page?: number; page_size?: number; search?: string; status?: string; organization_id?: number } = {}) {
    return api.get<{ list: any[]; total: number; page: number; page_size: number }>('/admin/repositories', { params })
  },
  updateRepository(id: number, data: { status?: string }) {
    return api.put<any>(`/admin/repositories/${id}`, data)
  },
  deleteRepository(id: number) {
    return api.delete<null>(`/admin/repositories/${id}`)
  },

  // organizations
  listOrganizations(params: { page?: number; page_size?: number; search?: string; status?: number } = {}) {
    return api.get<{ list: any[]; total: number; page: number; page_size: number }>('/admin/organizations', { params })
  },
  updateOrganization(id: number, data: { status?: number }) {
    return api.put<any>(`/admin/organizations/${id}`, data)
  },
  deleteOrganization(id: number) {
    return api.delete<null>(`/admin/organizations/${id}`)
  },

  // gpu models
  listGpuModels() {
    return api.get<any[]>('/admin/gpu-models')
  },
  createGpuModel(data: any) {
    return api.post<any>('/admin/gpu-models', data)
  },
  updateGpuModel(id: number, data: any) {
    return api.put<any>(`/admin/gpu-models/${id}`, data)
  },
  deleteGpuModel(id: number) {
    return api.delete<null>(`/admin/gpu-models/${id}`)
  },

  // platforms
  listPlatforms() {
    return api.get<any[]>('/admin/platforms')
  },
  createPlatform(data: any) {
    return api.post<any>('/admin/platforms', data)
  },
  updatePlatform(id: number, data: any) {
    return api.put<any>(`/admin/platforms/${id}`, data)
  },
  deletePlatform(id: number) {
    return api.delete<null>(`/admin/platforms/${id}`)
  },

  // tags
  listTags() {
    return api.get<any[]>('/admin/tags')
  },
  createTag(data: any) {
    return api.post<any>('/admin/tags', data)
  },
  updateTag(id: number, data: any) {
    return api.put<any>(`/admin/tags/${id}`, data)
  },
  deleteTag(id: number) {
    return api.delete<null>(`/admin/tags/${id}`)
  },
}

export const adminApiKeyApi = {
  list() {
    return api.get<{ list: AdminApiKey[] }>('/admin/api-keys')
  },

  create(name: string, scopes: string[]) {
    return api.post<AdminApiKey>('/admin/api-keys', { name, scopes })
  },

  update(id: number, data: { name?: string; scopes?: string[]; status?: number }) {
    return api.put<AdminApiKey>(`/admin/api-keys/${id}`, data)
  },

  remove(id: number) {
    return api.delete<null>(`/admin/api-keys/${id}`)
  },
}

export default adminApi
